import React, { useState } from 'react';
import { Modal, Table, Button, Tag, Select, Input, message, Space, Tooltip } from 'antd';
import { Plus, User, Clock, MessageSquare } from 'lucide-react';
import { useFeedbackStore } from '~/store/feedback.store';
import { useGetFeedback, useUpdateFeedback } from '~/services/feedback/feedback.hooks';
import { TFeedback } from '~/types/feedback';
import { useAuth } from '~/store';
import { Role } from '~/utils/constants';
import type { ColumnsType } from 'antd/es/table';
import { NewFeedbackForm } from './new-feedback-form';

const { TextArea } = Input;

const getStatusColor = (status: string) => {
  switch (status) {
    case 'OPEN': return 'red';
    case 'IN_PROGRESS': return 'orange';
    case 'COMPLETED': return 'green';
    default: return 'default';
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const FeedbackModal: React.FC = () => {
  const { isModalOpen, closeModal, openNewFeedback } = useFeedbackStore();
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [editingComments, setEditingComments] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  
  const { data: feedbackData, isLoading } = useGetFeedback({ 
    pageSize: 50,
    status: statusFilter as any 
  });
  
  const updateFeedbackMutation = useUpdateFeedback();

  const isSuperAdmin = user?.role === Role.SUPER_ADMIN;

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await updateFeedbackMutation.mutateAsync({ id, data: { status: status as any } });
      message.success('Status updated successfully');
    } catch (error) {
      message.error('Failed to update status');
    }
  };

  const handleCommentSave = async (id: string) => {
    try {
      await updateFeedbackMutation.mutateAsync({ 
        id, 
        data: { adminComments: commentText } 
      });
      message.success('Comments saved successfully');
      setEditingComments(null);
      setCommentText('');
    } catch (error) {
      message.error('Failed to save comments');
    }
  };

  const columns: ColumnsType<TFeedback> = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '40%',
      render: (text: string) => (
        <div style={{ maxWidth: 300, wordWrap: 'break-word' }}>
          {text.length > 100 ? (
            <Tooltip title={text}>
              {text.substring(0, 100)}...
            </Tooltip>
          ) : (
            text
          )}
        </div>
      ),
    },
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => (
        <div style={{ fontSize: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Clock size={12} />
            {formatDate(date)}
          </div>
        </div>
      ),
    },
   {
  title: 'User',
  dataIndex: 'user',
  key: 'user',
  width: 120,
  render: (user: any) => (
    <div style={{ fontSize: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <User size={12} />
        <div>
          <div>
            {user?.firstName && user?.lastName ? 
              `${user.firstName} ${user.lastName}` : 
              user?.username || 'Unknown User'}
          </div>
          <div style={{ color: '#666', fontSize: '11px' }}>
            {user?.role || 'N/A'}
          </div>
        </div>
      </div>
    </div>
  ),
},
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: string, record: TFeedback) => (
        isSuperAdmin ? (
          <Select
            value={status}
            onChange={(value) => handleStatusChange(record.id, value)}
            style={{ width: '100%' }}
            size="small"
          >
            <Select.Option value="OPEN">
              <Tag color="red">Open</Tag>
            </Select.Option>
            <Select.Option value="IN_PROGRESS">
              <Tag color="orange">In Progress</Tag>
            </Select.Option>
            <Select.Option value="COMPLETED">
              <Tag color="green">Completed</Tag>
            </Select.Option>
          </Select>
        ) : (
          <Tag color={getStatusColor(status)}>
            {status.replace('_', ' ')}
          </Tag>
        )
      ),
    },
    {
      title: 'Comments',
      dataIndex: 'adminComments',
      key: 'adminComments',
      width: '25%',
      render: (comments: string, record: TFeedback) => (
        isSuperAdmin ? (
          <div>
            {editingComments === record.id ? (
              <div style={{ width: '100%' }}>
                <TextArea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  rows={2}
                  style={{ marginBottom: 8 }}
                />
                <Space>
                  <Button size="small" onClick={() => handleCommentSave(record.id)}>
                    Save
                  </Button>
                  <Button size="small" onClick={() => setEditingComments(null)}>
                    Cancel
                  </Button>
                </Space>
              </div>
            ) : (
              <div 
                onClick={() => {
                  setEditingComments(record.id);
                  setCommentText(comments || '');
                }}
                style={{ 
                  cursor: 'pointer',
                  minHeight: '20px',
                  padding: '4px',
                  border: '1px dashed #d9d9d9',
                  borderRadius: '4px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <MessageSquare size={12} />
                  {comments || 'Click to add comments...'}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ fontSize: '12px' }}>
            {comments || '-'}
          </div>
        )
      ),
    },
  ];

  return (
    <>
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>App Feedback & Bug Reports</span>
            <Space>
              <Select
                placeholder="Filter by status"
                allowClear
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 150 }}
              >
                <Select.Option value="OPEN">Open</Select.Option>
                <Select.Option value="IN_PROGRESS">In Progress</Select.Option>
                <Select.Option value="COMPLETED">Completed</Select.Option>
              </Select>
              <Button 
                type="primary" 
                icon={<Plus size={16} />}
                onClick={openNewFeedback}
              >
                New Feedback
              </Button>
            </Space>
          </div>
        }
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        width="90%"
        style={{ maxWidth: 1200 }}
        destroyOnClose
      >
        <Table
          columns={columns}
          dataSource={feedbackData?.data?.items || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} feedback items`,
          }}
          scroll={{ x: 800 }}
          size="middle"
        />
      </Modal>
      
      <NewFeedbackForm />
    </>
  );
};
